<template>
  <div>
    <TimeField
      :id="idFrom"
      :value="modelValue[0]"
      :time-format="timeFormat"
      :min-value="minValue"
      :max-value="maxValue - 1"
      :icon="hideIcons ? null : TimeIcon"
      :light="light"
      :class="inputClasses[0]"
      :blocked-time-slots="blockedTimeSlots"
      placeholder="From"
      aria-label="Start time"
      :blocked-label="blockedTimeSlotLabel"
      :placeholder-value="timeFieldPlaceholderValue"
      :data-test-id="dataTestIdFrom"
      @input="change(0, +$event)"
      @selectClick="$emit('selectClick', 'hour_from')"
    />
    <TimeField
      :id="idTo"
      :value="modelValue[1]"
      :time-format="timeFormat"
      :min-value="modelValue[0] + 1"
      :icon="hideIcons ? null : TimeIcon"
      :light="light"
      :class="inputClasses[1]"
      :time-options="timeToOptions"
      placeholder="To"
      aria-label="End time"
      :placeholder-value="timeFieldPlaceholderValue"
      :blocked-label="blockedTimeSlotLabel"
      :is-disabled="(!modelValue[0] && selectTimeFromBeforeTimeTo) || areTimeToOptionsEmpty"
      :data-test-id="dataTestIdTo"
      @input="change(1, +$event)"
      @selectClick="$emit('selectClick', 'hour_to')"
    />
  </div>
</template>

<script>
import TimeIcon from '@tagvenue/icons/24/line/time/clock.svg';
import { markRaw } from 'vue';

import { timeFieldPlaceholderValue, timeSlotsMaxValue } from '@/services/date.js';
import { getMaximumSelectableToSlot, getTimeSlots } from '@/services/hours-map';

import TimeField from './TimeField.vue';

export default {
  name: 'TimeRange',
  emits: ['change', 'update:modelValue', 'selectClick'],
  components: { TimeField },

  props: {
    // eslint-disable-next-line vue/require-default-prop
    modelValue: {
      type: Array,
      validator: (value) => value.length === 2,
    },
    inputClasses: {
      type: Array,
      validator: (value) => value.length === 2,
      default: () => ['', ''],
    },
    showDuration: {
      type: Boolean,
      default: true,
    },
    // eslint-disable-next-line vue/require-default-prop
    timeFormat: {
      type: Number,
      validator(value) {
        return value === 12 || value === 24;
      },
    },
    minValue: {
      type: Number,
      default: -Infinity,
    },
    maxValue: {
      type: Number,
      default: Infinity,
    },
    blockedTimeSlots: {
      type: Array,
      default: () => [], // Array of tuples with 2 elements
    },
    availableTimeSlots: {
      type: Array,
      default: () => [], // Array of tuples with 2 elements
    },
    blockedTimeSlotLabel: {
      type: String,
      default: 'blocked',
    },
    selectTimeFromBeforeTimeTo: {
      type: Boolean,
      default: false,
    },

    dataTestIdFrom: {
      type: String,
      default: null,
    },
    dataTestIdTo: {
      type: String,
      default: null,
    },
    idFrom: {
      type: String,
      default: null,
    },
    idTo: {
      type: String,
      default: null,
    },
    autoSelectToField: {
      type: Boolean,
      default: false,
    },
    hideIcons: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      TimeIcon: markRaw(TimeIcon),
      pikaday: null,
      timeSlots: getTimeSlots(this.timeFormat),
      timeSlotsMaxValue,
      timeFieldPlaceholderValue,
    };
  },

  computed: {
    timeToMaxValue() {
      return getMaximumSelectableToSlot(this.availableTimeSlots, this.blockedTimeSlots, this.modelValue[0], this.maxValue);
    },

    areTimeToOptionsEmpty() {
      return Object.keys(this.timeToOptions).length === 0;
    },

    timeToOptions() {
      if (!this.showDuration) {
        return null; // Options will be generated in TimeField
      }
      const minValue = this.modelValue[0] + 1;

      return Object.keys(this.timeSlots)
        .filter(key => minValue <= key && key <= this.timeToMaxValue)
        .reduce((acc, key) => ({
          ...acc,
          [key]: {
            label: this.getTimeLabel(key, this.modelValue[0]),
          },
        }), {});
    },
  },

  methods: {
    change(key, value) {
      const parsedValue = value === timeFieldPlaceholderValue ? null : value;
      const result = [...this.modelValue];
      result[key] = parsedValue;

      // auto select time to, if not set yet and time from was provided
      if (this.autoSelectToField && parsedValue && key === 0 && !this.modelValue[1]) {
        result[1] = parsedValue + 2;
      }

      // unselect time to if time from is now bigger
      if (this.autoSelectToField && parsedValue && key === 0 && value >= result[1]) {
        result[1] = null;
      }

      // auto select time from, if not set yet and time to was provided
      if (this.autoSelectToField && parsedValue && key === 1 && !this.modelValue[0] && parsedValue - 2 >= 0) {
        result[0] = parsedValue - 2;
      }

      this.$emit('change', result);
      this.$emit('update:modelValue', result);
    },

    getDuration(time, minHour) {
      return (time - minHour) / 2;
    },

    getTimeLabel(time, minHour) {
      const showDuration = typeof minHour === 'number';
      if (!showDuration) {
        return this.timeSlots[time];
      }
      const duration = this.getDuration(time, minHour);
      const hourLabel = duration === 1 ? 'hr' : 'hrs';

      return `${this.timeSlots[time]} (${duration} ${hourLabel})`;
    },
  },
};
</script>

<style scoped>

</style>
